import React, { useEffect, useState } from 'react';
import { useFilters } from '../context/FilterContext.tsx';
import { Reviewer, ReviewType, ReviewerDecisionFilter } from '../types';
import { reviewService } from '../services/reviewService.ts';
import { categoryService } from '../services/categoryService.ts';
import { useProject } from '../context/ProjectContext.tsx';

export function FilterControls() {
    const { lit_filter, setLitFilter } = useFilters();
    const [reviewers, setReviewers] = useState<Reviewer[]>([]);
    const [categories, setCategories] = useState<string[]>([]);
    const [categoryValueMap, setCategoryValueMap] = useState<Record<string, string[]>>({});
    const { currentProject } = useProject();

    const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);

    useEffect(() => {
        fetchReviewers();
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => {
            fetchCategories();
        }, 300);
        return () => clearTimeout(timer);
    }, [lit_filter]);

    useEffect(() => {
        if (lit_filter.category_filters && lit_filter.category_filters.length > 0) {
            lit_filter.category_filters.forEach((filter) => {
                if (filter.category) {
                    fetchCategoryValues(filter.category);
                }
            });
        }
    }, []);

    useEffect(() => {
        if (currentProject) {
            setLitFilter({
                ...lit_filter,
                project_id: currentProject.id,
                page: 1,
            });
        }
    }, [currentProject]);

    const fetchReviewers = async () => {
        const data = await reviewService.getReviewer();
        setReviewers(data);
    };

    const fetchCategories = async () => {
        const categoriesList = await categoryService.getCategories(lit_filter);
        setCategories(categoriesList);
    };

    const fetchCategoryValues = async (category: string) => {
        try {
            const values = await categoryService.getCategoryValues(category, lit_filter);
            setCategoryValueMap((prev) => ({
                ...prev,
                [category]: values,
            }));
        } catch (error) {
            console.error(`Error fetching values for category ${category}:`, error);
        }
    };

    const handleCategoryFilterChange = async (
        index: number,

        field: 'category' | 'values' | 'exclude',
        value: string | string[] | boolean
    ) => {
        const newFilters = [...(lit_filter.category_filters || [])];

        if (field === 'category') {
            const categoryValue = value as string;
            if (categoryValue && !categoryValueMap[categoryValue]) {
                await fetchCategoryValues(categoryValue);
            }

            // When changing a category, remove all subsequent filters
            newFilters[index] = {
                category: categoryValue,
                values: [],
                exclude: false,
            };

            // Remove all filters after the current one
            const updatedFilters = newFilters.slice(0, index + 1);

            setLitFilter({
                ...lit_filter,
                category_filters: updatedFilters,
                page: 1,
            });

            // Refresh category values for all remaining filters
            updatedFilters.forEach((filter, idx) => {
                if (filter.category && idx >= index) {
                    fetchCategoryValues(filter.category);
                }
            });
        } else if (field === 'exclude') {
            newFilters[index] = {
                ...newFilters[index],
                exclude: value as boolean,
            };

            setLitFilter({
                ...lit_filter,
                category_filters: newFilters,
                page: 1,
            });
        } else {
            newFilters[index] = {
                ...newFilters[index],
                values: value as string[],
            };

            // When changing values, refresh all subsequent filters' values
            setLitFilter({
                ...lit_filter,
                category_filters: newFilters,
                page: 1,
            });

            // Refresh category values for all subsequent filters
            for (let i = index + 1; i < newFilters.length; i++) {
                if (newFilters[i].category) {
                    fetchCategoryValues(newFilters[i].category);
                }
            }
        }
    };

    const addCategoryFilter = () => {
        setLitFilter({
            ...lit_filter,
            category_filters: [
                ...(lit_filter.category_filters || []),
                { category: '', values: [], exclude: false },
            ],
            page: 1,
        });
    };

    const addReviewerDecisionFilter = () => {
        setLitFilter({
            ...lit_filter,
            reviewer_decision_filters: [
                ...(lit_filter.reviewer_decision_filters || []),
                { reviewer_id: 0, decision: null, decision_operator: 'equals' },
            ],
            page: 1,
        });
    };

    const handleReviewerDecisionChange = (index: number, field: keyof ReviewerDecisionFilter, value: any) => {
        const newFilters = [...(lit_filter.reviewer_decision_filters || [])];
        newFilters[index] = {
            ...newFilters[index],
            [field]: value,
        };
        if (value != null) {
            setLitFilter({
                ...lit_filter,
                reviewer_decision_filters: newFilters,
                page: 1,
            });
        }
    };

    return (
        <div className="filter-controls">
            <div className="cagegory-row">
                <input
                    type="text"
                    placeholder="Search titles, abstracts, authors..."
                    value={lit_filter.search || ''}
                    onChange={(e) => {
                        setLitFilter({
                            ...lit_filter,
                            search: e.target.value,
                            page: 1,
                        });
                    }}
                    className="search-input"
                />
                <input
                    type="text"
                    placeholder="Journal"
                    value={lit_filter.journal || ''}
                    onChange={(e) => {
                        setLitFilter({
                            ...lit_filter,
                            journal: e.target.value,
                            page: 1,
                        });
                    }}
                    className="search-input"
                />
                <input
                    type="text"
                    placeholder="Publisher"
                    value={lit_filter.publisher || ''}
                    onChange={(e) => {
                        setLitFilter({
                            ...lit_filter,
                            publisher: e.target.value,
                            page: 1,
                        });
                    }}
                    className="search-input"
                />
                <select
                    value={lit_filter.review_status || ''}
                    onChange={(e) => {
                        setLitFilter({
                            ...lit_filter,
                            review_status: e.target.value || null,
                            page: 1,
                        });
                    }}
                    className="filter-select">
                    <option value="">All Review Status</option>
                    <option value="accepted">Accepted</option>
                    <option value="rejected">Rejected</option>
                    <option value="needs_full_text">Needs Full Text</option>
                </select>

                <select
                    value={lit_filter.paper_status || ''}
                    onChange={(e) => {
                        setLitFilter({
                            ...lit_filter,
                            paper_status: e.target.value || null,
                            page: 1,
                        });
                    }}
                    className="filter-select">
                    <option value="">All Paper Status</option>
                    <option value="full_text_available">Full Text Available</option>
                    <option value="full_text_unavailable">Full Text Unavailable</option>
                </select>

                <select
                    multiple
                    value={lit_filter.reviewer_filters ? JSON.parse(lit_filter.reviewer_filters) : []}
                    onChange={(e) => {
                        const selectedReviewers = Array.from(e.target.selectedOptions, (option) =>
                            Number(option.value)
                        );
                        setLitFilter({
                            ...lit_filter,
                            reviewer_filters: JSON.stringify(selectedReviewers),
                            page: 1,
                        });
                    }}
                    className="filter-select reviewer-select">
                    {reviewers.map((reviewer) => (
                        <option key={reviewer.id} value={reviewer.id.toString()}>
                            {reviewer.name}
                        </option>
                    ))}
                </select>

                <select
                    value={lit_filter.view_mode || 'all'}
                    onChange={(e) => {
                        setLitFilter({
                            ...lit_filter,
                            view_mode: e.target.value as 'all' | 'disagreeing',
                            page: 1,
                        });
                    }}
                    className="filter-select">
                    <option value="all">All Reviews</option>
                    <option value="disagreeing">Disagreeing Reviews</option>
                </select>

                <select
                    value={lit_filter.review_type || ''}
                    onChange={(e) => {
                        setLitFilter({
                            ...lit_filter,
                            review_type: e.target.value as ReviewType,
                            page: 1,
                        });
                    }}
                    className="filter-select">
                    <option value="">All Reviews</option>
                    <option value="abstract">Abstract Reviews!</option>
                    <option value="full_paper">Full Paper Reviews</option>
                </select>
            </div>

            <div className="cagegory-row">
                {lit_filter.category_filters?.map((filter, index) => (
                    <div key={index} className="category-filter-group">
                        <select
                            value={filter.category}
                            onChange={(e) => handleCategoryFilterChange(index, 'category', e.target.value)}
                            className="filter-select">
                            <option value="">Select Category</option>
                            {categories.map((cat) => (
                                <option key={cat} value={cat}>
                                    {cat}
                                </option>
                            ))}
                        </select>

                        <div className="filter-mode-toggle">
                            <label>
                                <input
                                    type="checkbox"
                                    checked={filter.exclude || false}
                                    onChange={(e) =>
                                        handleCategoryFilterChange(index, 'exclude', e.target.checked)
                                    }
                                />
                                Exclude
                            </label>
                        </div>

                        <select
                            multiple
                            value={filter.values}
                            onChange={(e) => {
                                const selectedValues = Array.from(
                                    e.target.selectedOptions,
                                    (option) => option.value
                                );
                                handleCategoryFilterChange(index, 'values', selectedValues);
                            }}
                            className="filter-select">
                            {categoryValueMap[filter.category]?.map((val) => (
                                <option key={val} value={val}>
                                    {val}
                                </option>
                            ))}
                        </select>

                        {/* Only show remove button for the last filter */}
                        {index === (lit_filter.category_filters?.length ?? 0) - 1 && (
                            <button
                                onClick={() => {
                                    const newFilters = (lit_filter.category_filters || []).filter(
                                        (_, i) => i !== index
                                    );
                                    setLitFilter({
                                        ...lit_filter,
                                        category_filters: newFilters,
                                        page: 1,
                                    });
                                }}
                                className="remove-filter-button">
                                ×
                            </button>
                        )}
                    </div>
                ))}
                <button onClick={addCategoryFilter}>Add Category Filter</button>
            </div>

            <div className="advanced-filters-toggle">
                <button onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}>
                    {showAdvancedFilters ? 'Hide Advanced Filters' : 'Show Advanced Filters'}
                </button>
            </div>

            {showAdvancedFilters && (
                <div className="advanced-filters">
                    <h4>Reviewer Decision Filters</h4>
                    {lit_filter.reviewer_decision_filters?.map((filter, index) => (
                        <div key={index} className="reviewer-decision-filter">
                            <select
                                value={filter.reviewer_id || 0}
                                onChange={(e) =>
                                    handleReviewerDecisionChange(index, 'reviewer_id', Number(e.target.value))
                                }>
                                <option value={0}>Select Reviewer</option>
                                {reviewers.map((reviewer) => (
                                    <option key={reviewer.id} value={reviewer.id}>
                                        {reviewer.name}
                                    </option>
                                ))}
                            </select>

                            <select
                                value={filter.decision_operator || 'equals'}
                                onChange={(e) =>
                                    handleReviewerDecisionChange(index, 'decision_operator', e.target.value)
                                }>
                                <option value="equals">is</option>
                                <option value="not_equals">is not</option>
                            </select>

                            <select
                                value={filter.decision || ''}
                                onChange={(e) =>
                                    handleReviewerDecisionChange(index, 'decision', e.target.value || null)
                                }>
                                <option value="">Any Decision</option>
                                <option value="ACCEPTED">Accepted</option>
                                <option value="REJECTED">Rejected</option>
                                <option value="NEEDS_FULL_TEXT">Needs Full Text</option>
                            </select>

                            <button
                                onClick={() => {
                                    const newFilters =
                                        lit_filter.reviewer_decision_filters?.filter((_, i) => i !== index) ||
                                        [];
                                    setLitFilter({
                                        ...lit_filter,
                                        reviewer_decision_filters: newFilters,
                                        page: 1,
                                    });
                                }}>
                                ×
                            </button>
                        </div>
                    ))}
                    <button onClick={addReviewerDecisionFilter}>Add Reviewer Decision Filter</button>
                </div>
            )}
        </div>
    );
}
